import React, { useState, useEffect } from 'react'
import { X, Upload, Image, Trash2, Plus } from 'lucide-react'
import { categoryAPI } from '../../services/api'
import CategoryCreateDialog from './CategoryCreateDialog'

const PhotoUploadDialog = ({ isOpen, onClose, onUploadComplete }) => {
  const [selectedFiles, setSelectedFiles] = useState([])
  const [categories, setCategories] = useState([])
  const [subCategories, setSubCategories] = useState([])
  const [selectedCategory, setSelectedCategory] = useState('')
  const [selectedSubCategory, setSelectedSubCategory] = useState('')
  const [loadingCategories, setLoadingCategories] = useState(false)
  const [loadingSubCategories, setLoadingSubCategories] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState({})
  const [error, setError] = useState('')
  const [showCategoryCreateDialog, setShowCategoryCreateDialog] = useState(false)

  // Get auth token
  const getAuthToken = () => {
    return localStorage.getItem('token') || 'dev-token-' + Date.now()
  }

  // Fetch categories on component mount
  useEffect(() => {
    if (isOpen) {
      fetchCategories()
    }
  }, [isOpen])

  // Fetch subcategories when category changes
  useEffect(() => {
    if (selectedCategory) {
      fetchSubCategories(selectedCategory)
    } else {
      setSubCategories([])
      setSelectedSubCategory('')
    }
  }, [selectedCategory])

  const fetchCategories = async () => {
    try {
      setLoadingCategories(true)
      const response = await categoryAPI.getAll({ type: 'photo', isActive: true })

      if (response.data.success) {
        const categoriesData = response.data.data || []
        // Sort categories by name
        categoriesData.sort((a, b) => {
          const nameA = String(a.name || '').toLowerCase()
          const nameB = String(b.name || '').toLowerCase()
          return nameA.localeCompare(nameB)
        })
        setCategories(categoriesData)
      } else {
        setError('Failed to load categories')
      }
    } catch (error) {
      console.error('Error fetching categories:', error)
      setError('Failed to load categories')
    } finally {
      setLoadingCategories(false)
    }
  }

  const fetchSubCategories = async (category_id) => {
    try {
      setLoadingSubCategories(true)
      const response = await categoryAPI.getAll({
        parentId: category_id,
        type: 'photo',
        isActive: true
      })

      if (response.data.success) {
        const subCategoriesData = response.data.data || []
        // Sort subcategories by name
        subCategoriesData.sort((a, b) => {
          const nameA = String(a.name || '').toLowerCase()
          const nameB = String(b.name || '').toLowerCase()
          return nameA.localeCompare(nameB)
        })
        setSubCategories(subCategoriesData)
      } else {
        setSubCategories([])
      }
    } catch (error) {
      console.error('Error fetching subcategories:', error)
      setSubCategories([])
    } finally {
      setLoadingSubCategories(false)
    }
  }

  const handleCategoryCreated = (newCategory) => {
    // Refresh categories list
    fetchCategories()
    // Auto-select the newly created category
    setSelectedCategory(newCategory.id)
    setSelectedSubCategory('') // Reset subcategory
  }

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files)
    const imageFiles = files.filter(file => file.type.startsWith('image/'))

    if (imageFiles.length !== files.length) {
      setError('Only image files are allowed')
    } else {
      setError('')
    }

    setSelectedFiles(prev => [...prev, ...imageFiles])
    e.target.value = '' // Reset input
  }

  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index))
  }

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      setError('Please select at least one image')
      return
    }

    if (!selectedCategory) {
      setError('Please select a category')
      return
    }

    setUploading(true)
    setError('')
    const progress = {}
    const uploadResults = {
      success: 0,
      failed: 0,
      errors: []
    }

    try {
      // Upload files one by one
      for (let index = 0; index < selectedFiles.length; index++) {
        const file = selectedFiles[index]
        const formData = new FormData()
        formData.append('photo', file)
        formData.append('name', file.name.split('.')[0])
        formData.append('category', selectedCategory)
        if (selectedSubCategory) {
          formData.append('subcategory', selectedSubCategory)
        }

        progress[index] = 0
        setUploadProgress({ ...progress })

        // Use axios instance for progress reporting via onUploadProgress
        const { default: api } = await import('../../services/api')
        try {
          const res = await api.post('/photos', formData, {
            headers: { 'Content-Type': 'multipart/form-data', 'Authorization': `Bearer ${getAuthToken()}` },
            onUploadProgress: (evt) => {
              if (evt.total) {
                const pct = Math.round((evt.loaded * 100) / evt.total)
                progress[index] = pct
                setUploadProgress({ ...progress })
              }
            }
          })

          // Ensure 100% on completion
          progress[index] = 100
          setUploadProgress({ ...progress })
          uploadResults.success++

        } catch (err) {
          // Detect specific error types
          const status = err?.response?.status
          const code = err?.response?.data?.message || err?.response?.data?.error || ''
          const errorMessage = err?.response?.data?.message || err?.message || 'Unknown error'
          
          // Mark as failed but continue with other uploads
          progress[index] = 0
          setUploadProgress({ ...progress })
          uploadResults.failed++
          
          if (status === 409 || code === 'DUPLICATE_IMAGE' || errorMessage.includes('Duplicate')) {
            uploadResults.errors.push({ file: file.name, error: 'Duplicate image' })
          } else if (status === 0 || err?.code === 'ERR_NETWORK' || errorMessage.includes('network')) {
            uploadResults.errors.push({ file: file.name, error: 'Network error' })
          } else if (status >= 500) {
            uploadResults.errors.push({ file: file.name, error: 'Server error' })
          } else {
            uploadResults.errors.push({ file: file.name, error: errorMessage })
          }
          
          // Continue with next file instead of throwing
          console.warn(`Failed to upload ${file.name}:`, errorMessage)
        }
      }

      // Show summary message
      if (uploadResults.success > 0 && uploadResults.failed === 0) {
        // All succeeded
        setError('')
      } else if (uploadResults.success > 0 && uploadResults.failed > 0) {
        // Some succeeded, some failed
        setError(`${uploadResults.success} uploaded successfully, ${uploadResults.failed} failed. ${uploadResults.errors.map(e => `${e.file}: ${e.error}`).join('; ')}`)
      } else {
        // All failed
        setError(`All uploads failed. ${uploadResults.errors.map(e => `${e.file}: ${e.error}`).join('; ')}`)
      }

      // Reset form only if at least one upload succeeded
      if (uploadResults.success > 0) {
        setSelectedFiles([])
        setSelectedCategory('')
        setSelectedSubCategory('')
        setUploadProgress({})

        // Notify parent component
        if (onUploadComplete) {
          onUploadComplete()
        }

        // Close dialog if all succeeded, otherwise keep it open to show errors
        if (uploadResults.failed === 0) {
          onClose()
        }
      }
    } catch (error) {
      console.error('Error uploading photos:', error)
      setError('Upload failed: ' + error.message)
    } finally {
      setUploading(false)
    }
  }

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[550]">
      <div className="bg-white rounded-lg w-full max-w-2xl mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Upload Photos</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* File Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Images
            </label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleFileSelect}
                className="hidden"
                id="photo-upload-input"
              />
              <label
                htmlFor="photo-upload-input"
                className="cursor-pointer flex flex-col items-center"
              >
                <Upload className="w-12 h-12 text-gray-400 mb-4" />
                <p className="text-lg font-medium text-gray-900 mb-2">
                  Choose images to upload
                </p>
                <p className="text-sm text-gray-500">
                  Select multiple images at once. Supports JPG, PNG, GIF, WebP
                </p>
              </label>
            </div>
          </div>

          {/* Selected Files */}
          {selectedFiles.length > 0 && (
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-700 mb-3">
                Selected Files ({selectedFiles.length})
              </h3>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {selectedFiles.map((file, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div className="flex items-center space-x-3">
                      <Image className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="text-sm font-medium text-gray-900 truncate max-w-xs">
                          {file.name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {formatFileSize(file.size)}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => removeFile(index)}
                      className="p-1 hover:bg-gray-200 rounded transition-colors"
                      disabled={uploading}
                    >
                      <Trash2 className="w-4 h-4 text-gray-400" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Category Selection */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Category *
                </label>
                <button
                  type="button"
                  onClick={() => setShowCategoryCreateDialog(true)}
                  className="text-xs text-blue-600 hover:text-blue-700 flex items-center space-x-1"
                  disabled={uploading}
                >
                  <Plus className="w-3 h-3" />
                  <span>Add missing category</span>
                </button>
              </div>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                disabled={loadingCategories || uploading}
              >
                <option value="">Select a category</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
              {loadingCategories && (
                <p className="text-sm text-gray-500 mt-1">Loading categories...</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Subcategory
              </label>
              <select
                value={selectedSubCategory}
                onChange={(e) => setSelectedSubCategory(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                disabled={!selectedCategory || loadingSubCategories || uploading}
              >
                <option value="">Select a subcategory (optional)</option>
                {subCategories.map((subCategory) => (

                  <option key={subCategory.id} value={subCategory.id}>
                    {subCategory.name}
                  </option>
                ))}
              </select>
              {loadingSubCategories && (
                <p className="text-sm text-gray-500 mt-1">Loading subcategories...</p>
              )}
            </div>
          </div>

          {/* Error Message */}
          {error && (
            error === 'Duplicate image detected' ? (
              <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-700">This image already exists in your library. Duplicate uploads are not allowed.</p>
              </div>
            ) : (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )
          )}

          {/* Upload Progress */}
          {uploading && (
            <div className="mb-4">
              <p className="text-sm font-medium text-gray-700 mb-2">Uploading...</p>
              <div className="space-y-2">
                {selectedFiles.map((file, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <span className="text-xs text-gray-500 w-32 truncate">
                      {file.name}
                    </span>
                    <div className="flex-1 bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${uploadProgress[index] || 0}%` }}
                      />
                    </div>
                    <span className="text-xs text-gray-500 w-12">
                      {uploadProgress[index] || 0}%
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            disabled={uploading}
          >
            Cancel
          </button>
          <button
            onClick={handleUpload}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={uploading || selectedFiles.length === 0 || !selectedCategory}
          >
            {uploading ? 'Uploading...' : `Upload ${selectedFiles.length} Photo${selectedFiles.length !== 1 ? 's' : ''}`}
          </button>
        </div>
      </div>

      {/* Category Create Dialog */}
      <CategoryCreateDialog
        isOpen={showCategoryCreateDialog}
        onClose={() => setShowCategoryCreateDialog(false)}
        onCategoryCreated={handleCategoryCreated}
        type="photo"
      />
    </div>
  )
}

export default PhotoUploadDialog
